import React, { useState, useEffect } from "react";
import {
  Container,
  TextField,
  Typography,
  Box,
  Paper,
  Link,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { login } from "../../service/authService";
import logo from "../../assets/logo.svg";
import AppButton from "../../components/AppButton";
import Color from "../../components/Color";
import { useSnackbar } from "../../App";
import Loader from "../../components/Loader";

const Login = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [emailOrMobile, setEmailOrMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMeChecked, setRememberMeChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  //  validation errors
  const [errors, setErrors] = useState({
    emailOrMobile: "",
    password: "",
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ----------  saved credentials ----------
  useEffect(() => {
    const savedRememberMe = localStorage.getItem("rememberMe") === "true";
    const savedEmail = localStorage.getItem("savedEmail") || "";
    const savedPassword = localStorage.getItem("savedPassword") || "";

    setRememberMeChecked(savedRememberMe);
    if (savedRememberMe) {
      setEmailOrMobile(savedEmail);
      setPassword(savedPassword);
    }
  }, []);

  // ---------- Login ----------
  const handleLogin = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!emailOrMobile.trim()) {
      newErrors.emailOrMobile = "Email or mobile number is required";
    }

    if (!password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);

    try {
      const payload = {
        emailOrMobile: emailOrMobile.trim(),
        password,
        remember: rememberMeChecked,
      };

      const res = await login(payload);
      const { access_token, user } = res.data;

      // ---- Store auth data ----
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("role", user.role_id);
      localStorage.setItem("name", user.name);
      localStorage.setItem("email", user.email);
      localStorage.setItem("branch", user.branch_id);
      localStorage.setItem("mobile", user.mobile);
      localStorage.setItem("roleName", user.role_name);

      // ---- Remember me ----
      if (rememberMeChecked) {
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("savedEmail", emailOrMobile.trim());
        localStorage.setItem("savedPassword", password);
      } else {
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("savedEmail");
        localStorage.removeItem("savedPassword");
      }

      setLoading(false);
      showSnackbar("Login successful!", "success");

      if (user.role_id === 3) {
        navigate("/sweet-factory");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
      showSnackbar(
        err?.response?.data?.message ||
          "Invalid credentials. Please check email/mobile or password.",
        "error"
      );
    }
  };

  return (
    <>
      {loading && <Loader />}

      <Container
        maxWidth="xs"
        sx={{
          px: 2,
          display: "flex",
          minHeight: "100vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 3,
            width: "100%",
            maxWidth: 500,
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              height: isMobile ? 100 : 130,
              display: "block",
              mx: "auto",
              mb: 2,
            }}
          />

          <Typography
            variant={isMobile ? "h6" : "h5"}
            align="center"
            gutterBottom
            color="#7C0BCC"
            fontWeight={600}
          >
            Login to Account
          </Typography>

          <form onSubmit={handleLogin} noValidate>
            <Box display="flex" flexDirection="column" gap={2} mt={3}>
              {/* Email / Mobile */}
              <Box>
                <Typography
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: "13px",
                  }}
                >
                  Email or Mobile*
                </Typography>
                <TextField
                  fullWidth
                  required
                  value={emailOrMobile}
                  onChange={(e) => {
                    setEmailOrMobile(e.target.value);
                    setErrors({ ...errors, emailOrMobile: "" });
                  }}
                  error={!!errors.emailOrMobile}
                  helperText={errors.emailOrMobile}
                  placeholder="Enter email or mobile"
                />
              </Box>

              {/* Password */}
              <Box>
                <Typography
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: Color.label.main,
                    fontSize: "13px",
                  }}
                >
                  Password*
                </Typography>
                <TextField
                  fullWidth
                  required
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors({ ...errors, password: "" });
                  }}
                  error={!!errors.password}
                  helperText={errors.password}
                  placeholder="Enter password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((p) => !p)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Remember & Forgot */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMeChecked}
                      onChange={(e) => setRememberMeChecked(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Remember me"
                  sx={{ marginLeft: -1 }} // optional: reduces default left margin for tighter look
                />
                <Link href="/forgot" underline="hover" color="#7C0BCC" sx={{ fontSize: "0.875rem" }}>
                  Forgot Password?
                </Link>
              </Box>

              {/* Login Button */}
              <AppButton
                type="submit"
                disabled={loading}
                fullWidth
                sx={{ height: "50px" }}
              >
                Login
              </AppButton>
            </Box>
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default Login;
